import { ActionIcon, Flex, Group, NumberInput, Radio } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import React, { FC, useEffect } from 'react';

import { useI18n } from '../../../../../../contexts/I18nProvider';
import TimeRecorderService from '../../../../../../services/TimeRecorderService';
import { UserResponse } from '../../../../../../types/api/response/user';
import {
  TimeRecorderUserOvertimeConfiguration,
  TimeRecorderUserOvertimeMode,
} from '../../../../../../types/types';
import handleErrorMessage from '../../../../../../utils/handleErrorMessage';
import { useUserTimeRecorderOvertimeInCounterConfiguration } from './useUserTimeRecorderOvertimeInCounterConfiguration';

interface UserTimeRecorderOvertimeInCounterConfigurationProps {
  user: UserResponse;
}

const UserTimeRecorderOvertimeInCounterConfiguration: FC<
  UserTimeRecorderOvertimeInCounterConfigurationProps
> = ({ user }) => {
  const { t } = useI18n();

  const { configuration, refetch } =
    useUserTimeRecorderOvertimeInCounterConfiguration({
      userId: user.id,
      companyId: user.companyId!,
    });

  const { mutate, isLoading } = useMutation({
    mutationFn: (payload: TimeRecorderUserOvertimeConfiguration) =>
      TimeRecorderService.updateUserOvertimeInCounterConfiguration(payload),
    onSuccess: () => {
      refetch();
      showNotification({
        id: `updateUserOvertimeInCounterConfiguration-successful`,
        message: t('w.success'),
        color: 'green',
        icon: <IconCheck />,
      });
    },
    onError: (error) =>
      showNotification({
        id: 'updateUserOvertimeInCounterConfiguration-error',
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      }),
  });

  const form = useForm<TimeRecorderUserOvertimeConfiguration>({
    initialValues: {
      userId: user.id,
      companyId: user.companyId!,
      mode: TimeRecorderUserOvertimeMode.MONTHLY,
      hours: null,
    },
  });

  useEffect(() => {
    if (configuration) {
      form.setValues((prev) => ({
        ...prev,
        mode: configuration.mode,
        hours: configuration.hours,
      }));
    }
  }, [configuration]);

  return (
    <form onSubmit={form.onSubmit((values) => mutate(values))}>
      <Flex direction="column" gap="xs">
        <Radio.Group {...form.getInputProps('mode')}>
          <Group mt="xs">
            <Radio
              size="xs"
              value={TimeRecorderUserOvertimeMode.WEEKLY}
              label={t('timeRecorder.overtime.switchToCounter.WEEKLY')}
            />
            <Radio
              size="xs"
              value={TimeRecorderUserOvertimeMode.MONTHLY}
              label={t('timeRecorder.overtime.switchToCounter.MONTHLY')}
            />
          </Group>
        </Radio.Group>
        <Flex gap="md" align="center">
          <NumberInput
            size="xs"
            min={0}
            placeholder={
              form.values.mode === TimeRecorderUserOvertimeMode.MONTHLY
                ? '151.67'
                : '35'
            }
            {...form.getInputProps('hours')}
          />
          <ActionIcon
            type="submit"
            aria-label="Save"
            variant="light"
            loading={isLoading}
          >
            <IconDeviceFloppy
              style={{ width: '70%', height: '70%' }}
              stroke={1.5}
            />
          </ActionIcon>
        </Flex>
      </Flex>
    </form>
  );
};

export { UserTimeRecorderOvertimeInCounterConfiguration };
